/**
 * 後台導覽設定（Phase 2 IA）
 * 權限與舊版 AdminLayout nav-tabs 一致，不擴權。
 *
 * 側欄排序原則：日常營運 → 班級 → 英檢／學習歷程／問卷 → 資料匯入 → 分析報表 → 內容 → 帳號 → 系統
 * 長清單收成三級：主選單 → 子群組（可收合）→ 頁面。短清單維持二級。
 *
 * @typedef {{ actualUserRole: string, isTeacher: boolean, hasAdminRights: boolean, canViewReport: boolean, canViewSurvey: boolean, accessProfile?: object }} AdminNavContext
 */

import { P } from './permissions';
import { getAdminNavSearchTerms } from './adminNavSearchGlossary';
import { canAccessAdminRoute } from './adminRouteAccess';
import { isDeputyManagerProfile, isEventLeadProfile, isEtManagerProfile, isJtManagerProfile, isOfficeStaffOpsDenied, canAccessWeeklyReports } from '../utils/accessControl';

function canAccessImportCenter(c) {
  if (
    isDeputyManagerProfile(c?.accessProfile)
    || isEventLeadProfile(c?.accessProfile)
    || isEtManagerProfile(c?.accessProfile)
    || isJtManagerProfile(c?.accessProfile)
  ) {
    return false;
  }
  return canAccessAdminRoute(c?.accessProfile, '/admin/import-center');
}

/**
 * visibility 鍵：
 * - all：凡可進後台者
 * - canViewReport：活動與預約（列表／明細）
 * - classes：班級與參與
 * - english：英檢與培力（考試行政）
 * - learningOutcomes：學習歷程、護照與成效分析
 * - surveyGroup：問卷側欄群組是否出現
 * - canViewSurvey：問卷管理子項
 * - adminOnly：需 hasAdminRights（admin／executive）
 * - roleAdmin：僅系統角色 admin
 * - opsDashboard：營運總覽（非一般授課老師）
 * - operationsAnalytics：營運分析或營運報表匯出
 */
export function isNavItemVisible(visibility, c) {
  // Phase 2：permission-based visibility（以 accessProfile.finalPermissions 為主）
  if (typeof visibility === 'string' && visibility.startsWith('perm:')) {
    const key = visibility.slice('perm:'.length);
    const set = c?.accessProfile?.permissionSet;
    return !!(set && set.has && set.has(key));
  }
  switch (visibility) {
    case 'all':
      return true;
    case 'canViewReport':
      return c.canViewReport;
    case 'classSection': {
      const set = c?.accessProfile?.permissionSet;
      const canClass =
        !!(set && set.has && (
          set.has(P.CAN_VIEW_CLASSES)
          || set.has(P.CAN_MANAGE_CLASSES)
          || set.has(P.CAN_MANAGE_CLASS_CREDIT)
        ));
      return c.hasAdminRights || c.isTeacher || canClass;
    }
    case 'classCredit': {
      const set = c?.accessProfile?.permissionSet;
      return !!(set && set.has && (
        set.has(P.CAN_MANAGE_CLASS_CREDIT) || set.has(P.CAN_MANAGE_CLASSES)
      ));
    }
    case 'classes': {
      const set = c?.accessProfile?.permissionSet;
      const canClass =
        !!(set && set.has && (set.has(P.CAN_VIEW_CLASSES) || set.has(P.CAN_MANAGE_CLASSES)));
      return c.hasAdminRights || c.isTeacher || canClass;
    }
    case 'english': {
      const set = c?.accessProfile?.permissionSet;
      const canEnglish =
        !!(set && set.has && (
          set.has(P.CAN_VIEW_ENGLISH_TEST_METRICS) ||
          set.has(P.CAN_VIEW_ENGLISH_TESTS) ||
          set.has(P.CAN_MANAGE_LEARNING_PARTNER_ADMIN)
        ));
      return c.hasAdminRights || canEnglish;
    }
    case 'englishLearningPassport': {
      const set = c?.accessProfile?.permissionSet;
      return !!(set && set.has && (
        set.has(P.CAN_VIEW_ENGLISH_LEARNING_PASSPORTS)
        || set.has(P.CAN_REVIEW_ENGLISH_LEARNING_SUBMISSIONS)
        || set.has(P.CAN_MANAGE_ENGLISH_LEARNING_PASSPORTS)
      ));
    }
    case 'learningOutcomes': {
      const set = c?.accessProfile?.permissionSet;
      const canLearningOutcomes =
        !!(set && set.has && (
          set.has(P.CAN_VIEW_ENGLISH_TEST_TRACKING) ||
          set.has(P.CAN_MANAGE_ENGLISH_TEST_TRACKING) ||
          set.has(P.CAN_VIEW_ENGLISH_LEARNING_PASSPORTS) ||
          set.has(P.CAN_REVIEW_ENGLISH_LEARNING_SUBMISSIONS) ||
          set.has(P.CAN_VIEW_LEARNING_ANALYTICS) ||
          set.has(P.CAN_MANAGE_LEARNING_ANALYTICS_SETTINGS)
        ));
      return c.hasAdminRights || canLearningOutcomes;
    }
    case 'englishLearningJourney': {
      if (isDeputyManagerProfile(c?.accessProfile)) return false;
      const set = c?.accessProfile?.permissionSet;
      const canLj =
        !!(set && set.has && (
          set.has(P.CAN_VIEW_ENGLISH_TEST_TRACKING) ||
          set.has(P.CAN_MANAGE_ENGLISH_TEST_TRACKING)
        ));
      return c.hasAdminRights || canLj;
    }
    case 'surveyGroup':
      return c.hasAdminRights || c.canViewSurvey;
    case 'canViewSurvey':
      return c.canViewSurvey;
    case 'adminOnly':
      return c.hasAdminRights;
    case 'roleAdmin':
      return c?.actualUserRole === 'admin' || !!c?.accessProfile?.isAdmin;
    case 'importCenter':
      return canAccessImportCenter(c);
    case 'opsDashboard':
      if (isDeputyManagerProfile(c?.accessProfile) || isOfficeStaffOpsDenied(c?.accessProfile)) return false;
      if (c.actualUserRole === 'teacher' && !c.hasAdminRights) return false;
      // 工讀生不進營運總覽（活動工讀側欄另以 leaf allowlist 收斂）
      if (c.actualUserRole === 'worker') return false;
      return true;
    case 'analyticsHub':
      return canAccessAdminRoute(c?.accessProfile, '/admin/analytics');
    case 'operationsAnalytics':
      return (
        c?.accessProfile?.permissionSet?.has?.(P.CAN_VIEW_ANALYTICS) ||
        c?.accessProfile?.permissionSet?.has?.(P.CAN_EXPORT_REPORTS) ||
        c.hasAdminRights
      );
    case 'weeklyReports':
      return canAccessWeeklyReports(c?.accessProfile);
    case 'accountNav':
      if (c?.accessProfile?.permissionSet?.has(P.CAN_MANAGE_ACCOUNTS)) return true;
      return !!c?.accessProfile?.role;
    default:
      return false;
  }
}

/**
 * 活動工讀（event_ops）：側欄僅活動列表（不可變更密碼，避免帳密漂移）。
 * @param {AdminNavContext} c
 */
export function isEventOpsWorkerMenu(c) {
  return c?.actualUserRole === 'worker'
    && (c?.accessProfile?.workerLevel || 'event_ops') === 'event_ops';
}

/** @deprecated 舊名；改呼叫 isEventOpsWorkerMenu */
export function isWorkerRestrictedMenu(c) {
  return isEventOpsWorkerMenu(c);
}

/** 活動工讀允許的側欄 leaf id */
export const EVENT_OPS_WORKER_NAV_LEAF_IDS = new Set(['events-list']);

/** @deprecated 舊常數；活動工讀改用 EVENT_OPS_WORKER_NAV_LEAF_IDS */
export const WORKER_NAV_LEAF_IDS = EVENT_OPS_WORKER_NAV_LEAF_IDS;

/**
 * 依角色決定側欄預設展開的 section id（P1：避免首次進入全部收合）。
 * @param {AdminNavContext} c
 * @param {AdminNavSection[]} visibleSections
 * @returns {Set<string>}
 */
export function getDefaultExpandedSectionIds(c, visibleSections) {
  const ids = new Set();
  const role = c?.actualUserRole;
  const teacherLevel = c?.accessProfile?.teacherLevel;
  const workerLevel = c?.accessProfile?.workerLevel;

  if (role === 'leader') {
    ids.add('events');
  } else if (role === 'teacher' && teacherLevel === 'regular') {
    ids.add('classes');
  } else if (role === 'office_staff') {
    ids.add('events');
  } else if (role === 'worker') {
    if (workerLevel === 'bestep_ops') ids.add('english');
    else if (workerLevel === 'passport_ops') ids.add('learning-outcomes');
    else if (workerLevel === 'content_editor') ids.add('content');
    else ids.add('events');
    // 活動工讀側欄無帳號區（不可變更密碼）
    if ((workerLevel || 'event_ops') !== 'event_ops') ids.add('accounts');
  } else if (visibleSections.some((section) => section.id === 'events')) {
    // 管理員／主管只展開日常營運；目前頁面所屬選單由側欄另外展開
    ids.add('events');
  }

  return ids;
}

/**
 * 依角色回傳有權限的後台 home（AccessDenied CTA、登入落地對照用）。
 * @param {{ role?: string, teacherLevel?: string } | null | undefined} accessProfile
 */
export function getAdminRoleHomePath(accessProfile) {
  const role = accessProfile?.role;
  if (!role) return '/login';
  if (role === 'leader') return '/admin/et-grouping/my-sessions';
  if (role === 'teacher' && accessProfile.teacherLevel === 'regular') {
    return '/admin/classes';
  }
  if (role === 'office_staff') return '/admin/operations';
  if (role === 'worker') {
    const level = accessProfile.workerLevel || 'event_ops';
    if (level === 'bestep_ops') return '/admin/english-tests';
    if (level === 'content_editor') return '/admin/announcements';
    if (level === 'passport_ops') return '/admin/english-learning-passports';
    return '/admin/operations';
  }
  return '/admin/dashboard';
}

/**
 * @param {{ role?: string, teacherLevel?: string, workerLevel?: string } | null | undefined} accessProfile
 */
export function getAdminRoleHomeLabel(accessProfile) {
  const role = accessProfile?.role;
  if (role === 'leader') return '返回我的帶班場次';
  if (role === 'teacher' && accessProfile?.teacherLevel === 'regular') {
    return '返回班級概況';
  }
  if (role === 'office_staff') return '返回活動列表';
  if (role === 'worker') {
    const level = accessProfile?.workerLevel || 'event_ops';
    if (level === 'bestep_ops') return '返回英檢管理';
    if (level === 'content_editor') return '返回公告管理';
    if (level === 'passport_ops') return '返回實踐歷程護照';
    return '返回活動列表';
  }
  return '返回後台首頁';
}

export function canShowAdminNavItem(item, c) {
  if (item?.hiddenFromNav) return false;
  if (item?.visibility && !isNavItemVisible(item.visibility, c)) return false;
  if (item?.path && !canAccessAdminRoute(c?.accessProfile, item.path)) return false;
  // 活動工讀：僅保留活動列表
  if (isEventOpsWorkerMenu(c) && item?.id && !item?.children) {
    return EVENT_OPS_WORKER_NAV_LEAF_IDS.has(item.id);
  }
  return true;
}

/**
 * @typedef {{ id: string, label: string, path: string, matchPrefixes: string[], visibility: string, breadcrumbLabel?: string, pageTitle?: string, hiddenFromNav?: boolean, searchTerms?: string[], groupLabel?: string }} AdminNavLeaf
 * @typedef {{ id: string, label: string, visibility: string, expandable?: boolean, children?: AdminNavLeaf[], path?: string, matchPrefixes?: string[], pageTitle?: string, breadcrumbLabel?: string, hiddenFromNav?: boolean, hint?: string, searchTerms?: string[] }} AdminNavSection
 */

/** @type {AdminNavSection[]} */
export const ADMIN_NAV_SECTIONS = [
  {
    id: 'events',
    label: '活動與預約',
    expandable: true,
    hint: '日常簽到與違規請從活動列表進入明細分頁',
    hintRequiresLeafId: 'events-list',
    children: [
      {
        id: 'events-list',
        label: '活動列表',
        path: '/admin/operations',
        matchPrefixes: ['/admin/operations', '/admin/events'],
        visibility: 'canViewReport',
        pageTitle: '活動列表',
        breadcrumbLabel: '活動列表',
      },
      {
        id: 'events-participation-checkins',
        label: '簽到參與統計',
        path: '/admin/operations/participation',
        matchPrefixes: ['/admin/operations/participation'],
        visibility: 'canViewReport',
        pageTitle: '簽到參與統計',
        breadcrumbLabel: '簽到參與統計',
      },
      {
        id: 'events-compliance',
        label: '合規與違規',
        path: '/admin/violations',
        matchPrefixes: ['/admin/violations'],
        visibility: 'perm:can_manage_violations',
        pageTitle: '違規管理',
        breadcrumbLabel: '合規與違規',
      },
      {
        id: 'et-grouping',
        label: 'ET 分組',
        children: [
          {
            id: 'et-grouping-settings',
            label: 'ET 分組設定',
            path: '/admin/et-grouping/settings',
            matchPrefixes: ['/admin/et-grouping/settings'],
            visibility: 'perm:can_manage_et_grouping',
            pageTitle: 'ET 分組設定',
            breadcrumbLabel: 'ET 分組設定',
          },
          {
            id: 'et-task-templates',
            label: 'ET 任務模板',
            path: '/admin/et-grouping/tasks',
            matchPrefixes: ['/admin/et-grouping/tasks'],
            visibility: 'perm:can_manage_et_grouping',
            pageTitle: 'ET 任務模板',
            breadcrumbLabel: 'ET 任務模板',
          },
          {
            id: 'et-grouping-reports',
            label: 'ET 場次報表',
            path: '/admin/et-grouping/reports',
            matchPrefixes: ['/admin/et-grouping/reports'],
            visibility: 'perm:can_view_et_grouping',
            pageTitle: 'ET 場次報表彙總',
            breadcrumbLabel: 'ET 場次報表',
          },
          {
            id: 'et-leader-payroll',
            label: 'Leader 支薪報表',
            path: '/admin/et-grouping/payroll',
            matchPrefixes: ['/admin/et-grouping/payroll'],
            visibility: 'perm:can_view_et_grouping',
            pageTitle: 'Leader 支薪報表',
            breadcrumbLabel: 'Leader 支薪報表',
          },
          {
            id: 'et-student-trends',
            label: 'ET 學生趨勢',
            path: '/admin/et-grouping/student-trends',
            matchPrefixes: ['/admin/et-grouping/student-trends'],
            visibility: 'perm:can_view_et_grouping',
            pageTitle: 'ET 學生學期趨勢',
            breadcrumbLabel: 'ET 學生趨勢',
          },
          {
            id: 'et-leader-sessions',
            label: '我的帶班場次',
            path: '/admin/et-grouping/my-sessions',
            matchPrefixes: ['/admin/et-grouping/my-sessions'],
            visibility: 'perm:can_mark_et_session_tasks',
            pageTitle: '我的帶班場次',
            breadcrumbLabel: '我的帶班場次',
          },
        ],
      },
    ],
  },
  {
    id: 'classes',
    label: '班級與參與',
    visibility: 'classSection',
    expandable: true,
    children: [
      {
        id: 'classes-overview',
        label: '班級參與概況',
        path: '/admin/classes',
        matchPrefixes: ['/admin/classes'],
        visibility: 'classes',
        pageTitle: '班級參與概況',
        breadcrumbLabel: '班級列表',
      },
      {
        id: 'class-credit-allocation',
        label: '活動時數分配',
        path: '/admin/classes/credit-allocation',
        matchPrefixes: ['/admin/classes/credit-allocation'],
        visibility: 'classCredit',
        pageTitle: '活動時數分配',
        breadcrumbLabel: '活動時數分配',
        searchTerms: ['課堂加分', '時數', '計點'],
      },
    ],
  },
  {
    id: 'english',
    label: '英檢與培力',
    visibility: 'english',
    expandable: true,
    children: [
      {
        id: 'english-registration',
        label: '培力英檢管理',
        path: '/admin/english-test',
        matchPrefixes: ['/admin/english-test', '/admin/english-tests'],
        visibility: 'perm:can_view_english_tests',
        pageTitle: '培力英檢管理',
        breadcrumbLabel: '培力英檢管理',
      },
      {
        id: 'bestep-import',
        label: 'BESTEP 資料匯入',
        path: '/admin/english-test/import',
        matchPrefixes: ['/admin/english-test/import', '/admin/bestep/import'],
        visibility: 'english',
        pageTitle: 'BESTEP 資料匯入',
        breadcrumbLabel: 'BESTEP 資料匯入',
        // P14-5：匯入入口已整合至資料匯入中心；保留 route／麵包屑 meta，僅自側欄隱藏
        hiddenFromNav: true,
      },
    ],
  },
  {
    id: 'learning-outcomes',
    label: '學習歷程與成果',
    visibility: 'learningOutcomes',
    expandable: true,
    children: [
      {
        id: 'english-learning-passport',
        label: '英語實踐歷程護照',
        path: '/admin/english-learning-passports',
        matchPrefixes: ['/admin/english-learning-passports'],
        visibility: 'englishLearningPassport',
        pageTitle: '英語實踐歷程護照',
        breadcrumbLabel: '英語實踐歷程護照',
      },
      {
        id: 'learning-journey',
        label: '英語學習歷程中心',
        path: '/admin/learning-journey',
        matchPrefixes: [
          '/admin/learning-journey',
          '/admin/learning-journey-center',
        ],
        visibility: 'englishLearningJourney',
        pageTitle: '英語學習歷程中心',
        breadcrumbLabel: '英語學習歷程中心',
      },
      {
        id: 'speaking-diagnostic',
        label: 'ESAP 口說診斷',
        path: '/admin/speaking-diagnostic',
        matchPrefixes: ['/admin/speaking-diagnostic'],
        visibility: 'perm:can_view_learning_analytics',
        pageTitle: 'ESAP 口說診斷',
        breadcrumbLabel: 'ESAP 口說',
        searchTerms: ['speaking', '口說', 'fluency', 'pronunciation', 'EnglishCentral', 'ESAP', 'EMI'],
      },
      {
        id: 'learning-journey-import',
        label: '學習歷程資料匯入',
        path: '/admin/learning-journey/import',
        matchPrefixes: ['/admin/learning-journey/import'],
        visibility: 'englishLearningJourney',
        pageTitle: '學習歷程資料匯入',
        breadcrumbLabel: '學習歷程資料匯入',
        hiddenFromNav: true,
      },
      {
        id: 'learning-journey-ewl-sync',
        label: '英文寫作工坊（EWL）同步',
        path: '/admin/learning-journey/ewl-sync',
        matchPrefixes: ['/admin/learning-journey/ewl-sync'],
        visibility: 'englishLearningJourney',
        pageTitle: '英文寫作工坊（EWL）同步',
        breadcrumbLabel: 'EWL 同步',
        searchTerms: ['寫作工坊', 'EWL', '同步', 'ReservationInfo'],
      },
      {
        id: 'learning-journey-operations',
        label: '資料維運紀錄',
        path: '/admin/learning-journey/operations',
        matchPrefixes: ['/admin/learning-journey/operations'],
        visibility: 'perm:can_view_english_test_tracking',
        pageTitle: '資料維運紀錄',
        breadcrumbLabel: '資料維運紀錄',
        // P14-7：維運查詢入口已整合至資料匯入中心；保留 route／麵包屑 meta，僅自側欄隱藏
        hiddenFromNav: true,
      },
      {
        id: 'learning-analytics',
        label: '學習成效',
        children: [
          {
            id: 'la-report',
            label: '上呈',
            children: [
              {
                id: 'learning-analytics-overview',
                label: '學習成效總覽',
                path: '/admin/learning-analytics/overview',
                matchPrefixes: ['/admin/learning-analytics/overview'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '英語學習成效分析',
                breadcrumbLabel: '學習成效總覽',
              },
              {
                id: 'learning-analytics-kpi-report',
                label: 'B2 KPI 報表',
                path: '/admin/learning-analytics/kpi-report',
                matchPrefixes: ['/admin/learning-analytics/kpi-report'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: 'B2 KPI 一鍵報表',
                breadcrumbLabel: 'B2 KPI 報表',
              },
            ],
          },
          {
            id: 'la-people',
            label: '找人',
            children: [
              {
                id: 'learning-analytics-b1-plus',
                label: '接近 B2',
                path: '/admin/learning-analytics/b1-plus',
                matchPrefixes: ['/admin/learning-analytics/b1-plus'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '接近 B2',
                breadcrumbLabel: '接近 B2',
              },
              {
                id: 'learning-analytics-explore',
                label: '學生探索',
                path: '/admin/learning-analytics/explore',
                matchPrefixes: ['/admin/learning-analytics/explore'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '學生探索',
                breadcrumbLabel: '學生探索',
              },
              {
                id: 'learning-analytics-tracking',
                label: '追蹤名單',
                path: '/admin/learning-analytics/tracking-cohorts',
                matchPrefixes: ['/admin/learning-analytics/tracking-cohorts'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '追蹤名單',
                breadcrumbLabel: '追蹤名單',
              },
              {
                id: 'learning-analytics-students',
                label: '個人軌跡',
                path: '/admin/learning-analytics/students',
                matchPrefixes: ['/admin/learning-analytics/students'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '學生個人學習軌跡',
                breadcrumbLabel: '個人軌跡',
              },
            ],
          },
          {
            id: 'la-resources',
            label: '看資源',
            children: [
              {
                id: 'learning-analytics-offerings',
                label: '課／師／活動',
                path: '/admin/learning-analytics/offerings',
                matchPrefixes: ['/admin/learning-analytics/offerings'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '課程／教師／活動細項',
                breadcrumbLabel: '課／師／活動',
              },
              {
                id: 'learning-analytics-resources',
                label: '資源覆蓋',
                path: '/admin/learning-analytics/resources',
                matchPrefixes: ['/admin/learning-analytics/resources'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '資源參與覆蓋',
                breadcrumbLabel: '資源覆蓋',
              },
            ],
          },
          {
            id: 'la-compare',
            label: '比較與資料',
            children: [
              {
                id: 'learning-analytics-cohorts',
                label: '系所比較',
                path: '/admin/learning-analytics/cohorts',
                matchPrefixes: ['/admin/learning-analytics/cohorts'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '系所／入學年比較',
                breadcrumbLabel: '系所比較',
              },
              {
                id: 'learning-analytics-skills',
                label: '技能成長',
                path: '/admin/learning-analytics/skills',
                matchPrefixes: ['/admin/learning-analytics/skills'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '聽讀說寫技能成長',
                breadcrumbLabel: '技能成長',
              },
              {
                id: 'learning-analytics-raw',
                label: '資料匯出',
                path: '/admin/learning-analytics/raw-data',
                matchPrefixes: ['/admin/learning-analytics/raw-data'],
                visibility: 'perm:can_view_learning_analytics',
                pageTitle: '原始資料匯出',
                breadcrumbLabel: '資料匯出',
              },
              {
                id: 'learning-analytics-settings',
                label: '分析設定',
                path: '/admin/learning-analytics/settings',
                matchPrefixes: ['/admin/learning-analytics/settings'],
                visibility: 'perm:can_manage_learning_analytics_settings',
                pageTitle: '分析設定與資料版本',
                breadcrumbLabel: '分析設定',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'surveys',
    label: '問卷與回饋',
    visibility: 'surveyGroup',
    expandable: true,
    hint: '建議流程：問卷中心 → 啟用規則 → 作答／分析 → 資料品質維運',
    children: [
      {
        id: 'survey-center',
        label: '問卷中心（建立／發布）',
        path: '/admin/survey-center',
        matchPrefixes: ['/admin/survey-center'],
        visibility: 'perm:can_view_surveys',
        pageTitle: '問卷中心',
        breadcrumbLabel: '問卷中心',
      },
      {
        id: 'survey-rules-new',
        label: '啟用規則',
        path: '/admin/survey-rules',
        matchPrefixes: ['/admin/survey-rules'],
        visibility: 'perm:can_manage_survey_settings',
        pageTitle: '啟用規則',
        breadcrumbLabel: '啟用規則',
      },
      {
        id: 'survey-responses',
        label: '作答紀錄',
        path: '/admin/survey-responses',
        matchPrefixes: ['/admin/survey-responses'],
        visibility: 'perm:can_view_survey_responses',
        pageTitle: '問卷作答紀錄',
        breadcrumbLabel: '作答紀錄',
      },
      {
        id: 'survey-analytics',
        label: '統計分析',
        path: '/admin/survey-analytics',
        matchPrefixes: ['/admin/survey-analytics'],
        visibility: 'perm:can_view_survey_analytics',
        pageTitle: '問卷分析',
        breadcrumbLabel: '統計分析',
      },
      {
        id: 'survey-ops',
        label: '維運',
        children: [
          {
            id: 'survey-health',
            label: '資料品質（維運）',
            path: '/admin/survey-health',
            matchPrefixes: ['/admin/survey-health'],
            visibility: 'perm:can_view_survey_health',
            pageTitle: '問卷資料品質',
            breadcrumbLabel: '資料品質',
          },
          {
            id: 'survey-answer-mappings',
            label: '答案對照（維運）',
            path: '/admin/survey-answer-mappings',
            matchPrefixes: ['/admin/survey-answer-mappings'],
            visibility: 'perm:can_manage_survey_answer_mapping',
            pageTitle: '問卷答案對照',
            breadcrumbLabel: '答案對照',
          },
        ],
      },
      {
        id: 'survey-manage',
        label: '問卷管理（Legacy・已封存）',
        path: '/admin/surveys',
        matchPrefixes: ['/admin/surveys'],
        visibility: 'canViewSurvey',
        pageTitle: '問卷管理（Legacy）',
        breadcrumbLabel: '問卷管理',
        hiddenFromNav: true,
      },
      {
        id: 'survey-product',
        label: '問卷模組（請改用問卷中心）',
        path: '/admin/survey-module',
        matchPrefixes: ['/admin/survey-module'],
        visibility: 'perm:can_view_surveys',
        pageTitle: '問卷模組',
        breadcrumbLabel: '問卷模組',
        hiddenFromNav: true,
      },
    ],
  },
  {
    id: 'import-center',
    label: '資料匯入中心',
    visibility: 'importCenter',
    expandable: true,
    children: [
      {
        id: 'import-center-home',
        label: '匯入入口',
        path: '/admin/import-center',
        matchPrefixes: ['/admin/import-center'],
        visibility: 'importCenter',
        pageTitle: '資料匯入中心',
        breadcrumbLabel: '匯入入口',
      },
      {
        id: 'import-center-runs',
        label: '匯入紀錄',
        path: '/admin/import-center/runs',
        matchPrefixes: ['/admin/import-center/runs'],
        visibility: 'importCenter',
        pageTitle: '匯入紀錄中心',
        breadcrumbLabel: '匯入紀錄',
      },
    ],
  },
  {
    id: 'analytics',
    label: '營運分析與報表',
    visibility: 'analyticsHub',
    expandable: true,
    children: [
      {
        id: 'analytics-hub',
        label: '成效分析中心',
        path: '/admin/analytics',
        matchPrefixes: ['/admin/analytics'],
        visibility: 'analyticsHub',
        pageTitle: '成效分析中心',
        breadcrumbLabel: '成效分析中心',
      },
      {
        id: 'analytics-students',
        // 舊學號搜尋入口（已自側欄隱藏）；正式查詢請用「英語學習歷程中心」學生清單
        label: '學生學習歷程查詢',
        path: '/admin/analytics/students',
        matchPrefixes: ['/admin/analytics/students', '/admin/analytics/student/'],
        visibility: 'perm:can_view_english_test_tracking',
        pageTitle: '學生學習歷程查詢',
        breadcrumbLabel: '學生學習歷程查詢',
        hiddenFromNav: true,
      },
      {
        id: 'analytics-overview',
        label: '活動營運總覽',
        path: '/admin/analytics/overview',
        matchPrefixes: ['/admin/analytics/overview'],
        visibility: 'adminOnly',
        pageTitle: '活動營運總覽',
        breadcrumbLabel: '活動營運總覽',
      },
      {
        id: 'analytics-risk',
        label: '高風險行政追蹤',
        path: '/admin/analytics/risk',
        matchPrefixes: ['/admin/analytics/risk'],
        visibility: 'adminOnly',
        pageTitle: '高風險行政追蹤',
        breadcrumbLabel: '高風險行政追蹤',
      },
      {
        id: 'analytics-reports',
        label: '營運報表下載',
        path: '/admin/reports',
        matchPrefixes: ['/admin/reports'],
        visibility: 'perm:can_export_reports',
        pageTitle: '營運報表下載',
        breadcrumbLabel: '營運報表下載',
      },
      {
        id: 'analytics-teacher-dash',
        label: '我的教學儀表板',
        path: '/admin/teachers/dashboard',
        matchPrefixes: ['/admin/teachers/dashboard'],
        visibility: 'perm:can_view_analytics',
        pageTitle: '我的教學儀表板',
        breadcrumbLabel: '我的教學儀表板',
      },
    ],
  },
  {
    id: 'content',
    label: '內容與公告',
    expandable: true,
    children: [
      {
        id: 'announcements',
        label: '公告',
        path: '/admin/announcements',
        matchPrefixes: ['/admin/announcements'],
        visibility: 'perm:can_manage_announcements',
        pageTitle: '公告管理',
        breadcrumbLabel: '公告管理',
      },
      {
        id: 'weekly-reports',
        label: '英語中心週報',
        path: '/admin/weekly-reports',
        matchPrefixes: ['/admin/weekly-reports'],
        visibility: 'weeklyReports',
        pageTitle: '英語中心週報',
        breadcrumbLabel: '英語中心週報',
      },
      {
        id: 'student-content',
        label: '學生端內容',
        path: '/admin/student-content',
        matchPrefixes: ['/admin/student-content', '/admin/site-content', '/admin/page-content'],
        visibility: 'perm:can_manage_site_content',
        pageTitle: '學生端內容',
        breadcrumbLabel: '學生端內容',
      },
    ],
  },
  {
    id: 'accounts',
    label: '帳號與權限',
    visibility: 'accountNav',
    expandable: true,
    children: [
      {
        id: 'account-list',
        label: '帳號管理',
        path: '/admin/account',
        matchPrefixes: ['/admin/account', '/admin/accounts'],
        visibility: 'perm:can_manage_accounts',
        pageTitle: '帳號管理',
        breadcrumbLabel: '帳號管理',
      },
      {
        id: 'account-reset',
        label: '變更密碼',
        path: '/admin/account/reset',
        matchPrefixes: ['/admin/account/reset'],
        visibility: 'all',
        pageTitle: '變更密碼',
        breadcrumbLabel: '變更密碼',
      },
    ],
  },
  {
    id: 'system',
    label: '系統與稽核',
    expandable: true,
    children: [
      {
        id: 'system-dashboard',
        label: '營運總覽',
        path: '/admin/dashboard',
        matchPrefixes: ['/admin/dashboard'],
        visibility: 'opsDashboard',
        pageTitle: '營運總覽',
        breadcrumbLabel: '營運總覽',
      },
      {
        id: 'system-config',
        label: '設定',
        children: [
          {
            id: 'system-settings',
            label: '系統設定',
            path: '/admin/settings/system',
            matchPrefixes: ['/admin/settings/system', '/admin/system/settings'],
            visibility: 'perm:can_manage_settings',
            pageTitle: '系統設定',
            breadcrumbLabel: '系統設定',
          },
          {
            id: 'event-types-settings',
            label: '活動類型設定',
            path: '/admin/settings/event-types',
            matchPrefixes: ['/admin/settings/event-types'],
            visibility: 'perm:can_manage_events',
            pageTitle: '活動類型設定',
            breadcrumbLabel: '活動類型設定',
          },
          {
            id: 'system-email-templates',
            label: '郵件設定',
            path: '/admin/settings/email-templates',
            matchPrefixes: ['/admin/settings/email-templates'],
            visibility: 'perm:can_manage_settings',
            pageTitle: '郵件設定中心',
            breadcrumbLabel: '郵件設定',
          },
        ],
      },
      {
        id: 'system-audit',
        label: '稽核與維運',
        children: [
          {
            id: 'system-logs',
            label: '操作紀錄',
            path: '/admin/logs',
            matchPrefixes: ['/admin/logs'],
            visibility: 'perm:can_view_audit_logs',
            pageTitle: '操作紀錄',
            breadcrumbLabel: '操作紀錄',
          },
          {
            id: 'system-diagnostics',
            label: '系統診斷',
            path: '/admin/diagnostics',
            matchPrefixes: ['/admin/diagnostics'],
            visibility: 'perm:can_view_internal_diagnostics',
            pageTitle: '系統診斷',
            breadcrumbLabel: '系統診斷',
          },
          {
            id: 'system-ops-scripts',
            label: '維運腳本',
            path: '/admin/ops-scripts',
            matchPrefixes: ['/admin/ops-scripts'],
            visibility: 'roleAdmin',
            pageTitle: '維運腳本',
            breadcrumbLabel: '維運腳本',
            searchTerms: ['備份', 'backup', '寫作工坊', 'EWL', '同步', '腳本', '維運'],
          },
        ],
      },
      {
        id: 'system-trip-count',
        label: '旅遊分帳',
        path: '/admin/trip-counts',
        matchPrefixes: ['/admin/trip-counts'],
        visibility: 'perm:can_use_trip_count',
        pageTitle: '旅遊分帳',
        breadcrumbLabel: '旅遊分帳',
        searchTerms: ['分帳', '旅遊', 'TripCount', '結算', '轉帳'],
      },
    ],
  },
];

/** 有子項、且本身不是連結的節點（主選單或三級子群組） */
function isNavGroup(node) {
  return Array.isArray(node?.children) && node.children.length > 0 && !node.path;
}

/** 群組內第一個會出現在側欄的連結，供麵包屑使用 */
function firstVisibleLeafPath(node) {
  if (!node) return undefined;
  if (node.path && !node.hiddenFromNav) return node.path;
  for (const child of node.children || []) {
    const found = firstVisibleLeafPath(child);
    if (found) return found;
  }
  return undefined;
}

/** 將 section 轉成可迭代葉節（含子群組） */
function flattenLeaves(sections) {
  /** @type {{ section: AdminNavSection, group: AdminNavSection|null, leaf: AdminNavLeaf }[]} */
  const out = [];

  function walk(nodes, section, groupChain) {
    for (const node of nodes) {
      if (isNavGroup(node)) {
        if (!section) walk(node.children, node, []);
        else walk(node.children, section, groupChain.concat(node));
        continue;
      }
      if (!node.path) continue;
      const owner = section || node;
      out.push({
        section: owner,
        group: groupChain[0] || null,
        groupChain,
        leaf: section
          ? node
          : {
              id: node.id,
              label: node.label,
              path: node.path,
              matchPrefixes: node.matchPrefixes || [node.path],
              visibility: node.visibility,
              pageTitle: node.pageTitle,
              breadcrumbLabel: node.breadcrumbLabel,
            },
      });
    }
  }

  walk(sections, null, []);
  return out;
}

/**
 * 最長前綴匹配 pathname（用於 breadcrumb / 標題 / active）
 * @param {string} pathname
 * @param {string[]} prefixes
 */
function bestPrefixLength(pathname, prefixes) {
  let best = 0;
  for (const p of prefixes) {
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      best = Math.max(best, p.length);
    }
  }
  return best;
}

/**
 * @param {string} pathname
 * @param {AdminNavContext} ctx
 */
export function getAdminPageMeta(pathname, ctx) {
  const filtered = filterVisibleNav(ADMIN_NAV_SECTIONS, ctx);

  // 活動列表／簽到參與統計／活動明細（Phase 3）
  if (pathname === '/admin/operations' || pathname === '/admin/events') {
    const ev = filtered.find((s) => s.id === 'events');
    if (ev) {
      return {
        groupLabel: ev.label,
        pageTitle: '活動列表',
        breadcrumbLeaf: '活動列表',
        sectionId: 'events',
        childId: 'events-list',
      };
    }
  }
  if (pathname === '/admin/operations/participation') {
    const ev = filtered.find((s) => s.id === 'events');
    if (ev) {
      return {
        groupLabel: ev.label,
        pageTitle: '簽到參與統計',
        breadcrumbLeaf: '簽到參與統計',
        sectionId: 'events',
        childId: 'events-participation-checkins',
      };
    }
  }
  if (pathname.match(/^\/admin\/operations\/\d+\/checkin-kiosk$/)) {
    const ev = filtered.find((s) => s.id === 'events');
    if (ev) {
      return {
        groupLabel: ev.label,
        pageTitle: '現場簽到 Kiosk',
        breadcrumbLeaf: '現場簽到 Kiosk',
        breadcrumbParent: { label: '活動列表', to: '/admin/operations' },
        sectionId: 'events',
        childId: 'event-checkin-kiosk',
      };
    }
  }
  if (pathname.match(/^\/admin\/operations\/\d+$/)) {
    const ev = filtered.find((s) => s.id === 'events');
    if (ev) {
      return {
        groupLabel: ev.label,
        pageTitle: '活動明細',
        breadcrumbLeaf: '活動明細',
        breadcrumbParent: { label: '活動列表', to: '/admin/operations' },
        sectionId: 'events',
        childId: 'event-detail',
      };
    }
  }

  // 班級動態路由（優先於一般前綴匹配）
  if (pathname.startsWith('/admin/survey-module')) {
    const sur = filtered.find((s) => s.id === 'surveys');
    if (sur) {
      return {
        groupLabel: sur.label,
        pageTitle: pathname.includes('/responses')
          ? '問卷作答'
          : pathname.includes('/stats')
            ? '問卷統計'
            : '問卷模組',
        breadcrumbLeaf: pathname.includes('/responses')
          ? '作答資料'
          : pathname.includes('/stats')
            ? '統計'
            : '問卷模組',
        sectionId: 'surveys',
        childId: 'survey-product',
      };
    }
  }

  if (pathname === '/admin/english-test/import' || pathname === '/admin/bestep/import') {
    const english = filtered.find((s) => s.id === 'english');
    if (english) {
      return {
        groupLabel: english.label,
        pageTitle: 'BESTEP 資料匯入',
        breadcrumbLeaf: 'BESTEP 資料匯入',
        sectionId: 'english',
        childId: 'bestep-import',
      };
    }
  }

  if (
    pathname === '/admin/learning-journey/operations' ||
    pathname.startsWith('/admin/learning-journey/operations/')
  ) {
    const english = filtered.find((s) => s.id === 'english');
    if (english) {
      return {
        groupLabel: english.label,
        pageTitle: '資料維運紀錄',
        breadcrumbLeaf: '資料維運紀錄',
        sectionId: 'english',
        childId: 'learning-journey-operations',
      };
    }
  }

  if (pathname.startsWith('/admin/classes')) {
    const classesSection = filtered.find((s) => s.id === 'classes');
    if (classesSection) {
      const groupLabel = classesSection.label;
      if (pathname === '/admin/classes/credit-allocation') {
        return {
          groupLabel,
          pageTitle: '活動時數分配',
          breadcrumbLeaf: '活動時數分配',
          sectionId: 'classes',
          childId: 'class-credit-allocation',
        };
      }
      if (pathname.includes('/bestep')) {
        return {
          groupLabel,
          pageTitle: 'BESTEP',
          breadcrumbLeaf: 'BESTEP',
          sectionId: 'classes',
          childId: 'classes-bestep',
        };
      }
      if (pathname.match(/^\/admin\/classes\/[^/]+$/)) {
        return {
          groupLabel,
          pageTitle: '班級明細',
          breadcrumbLeaf: '班級明細',
          sectionId: 'classes',
          childId: 'classes-detail',
        };
      }
      if (pathname === '/admin/classes') {
        return {
          groupLabel,
          pageTitle: classesSection.pageTitle || '班級參與概況',
          breadcrumbLeaf: classesSection.breadcrumbLabel || '班級列表',
          sectionId: 'classes',
          childId: 'classes-overview',
        };
      }
    }
  }

  const pairs = flattenLeaves(filtered);
  let bestScore = -1;
  /** @type {{ section: AdminNavSection, leaf: AdminNavLeaf } | null} */
  let best = null;

  for (const pair of pairs) {
    const prefs = pair.leaf.matchPrefixes || [pair.leaf.path];
    const len = bestPrefixLength(pathname, prefs);
    if (len > bestScore) {
      bestScore = len;
      best = pair;
    }
  }

  if (!best || bestScore <= 0) {
    return {
      groupLabel: '後台',
      pageTitle: '後台',
      breadcrumbLeaf: pathname.replace(/^\/admin\/?/, '') || '總覽',
      sectionId: null,
      childId: null,
    };
  }

  // 學生 profile
  let breadcrumbLeaf = best.leaf.breadcrumbLabel || best.leaf.label;
  let pageTitle = best.leaf.pageTitle || best.leaf.label;
  if (pathname.startsWith('/admin/analytics/student/')) {
    const english = filtered.find((s) => s.id === 'english');
    if (english) {
      return {
        groupLabel: english.label,
        pageTitle: '學生學習歷程',
        breadcrumbLeaf: '學生學習歷程',
        sectionId: 'english',
        childId: 'learning-journey',
      };
    }
    breadcrumbLeaf = '學生學習歷程';
    pageTitle = '學生學習歷程';
  }

  if (pathname === '/admin' || pathname === '/admin/dashboard') {
    const sys = filtered.find((s) => s.id === 'system');
    if (sys) {
      return {
        groupLabel: sys.label,
        pageTitle: '營運總覽',
        breadcrumbLeaf: '營運總覽',
        sectionId: 'system',
        childId: 'system-dashboard',
      };
    }
    breadcrumbLeaf = '營運總覽';
    pageTitle = '營運總覽';
  }

  return {
    groupLabel: best.section.label,
    navGroupLabel: best.group?.label || null,
    navGroupPath: best.group ? firstVisibleLeafPath(best.group) : null,
    pageTitle,
    breadcrumbLeaf,
    sectionId: best.section.id,
    groupId: best.group?.id || null,
    groupIds: (best.groupChain || []).map((group) => group.id),
    childId: best.leaf.id,
  };
}

/**
 * @param {AdminNavSection[]} sections
 * @param {AdminNavContext} c
 * @returns {AdminNavSection[]}
 */
function treeHasLeafId(nodes, id) {
  for (const node of nodes || []) {
    if (node.id === id) return true;
    if (node.children && treeHasLeafId(node.children, id)) return true;
  }
  return false;
}

function filterNavNode(node, c) {
  if (isNavGroup(node)) {
    if (node.visibility && !isNavItemVisible(node.visibility, c)) return null;
    const children = node.children.map((child) => filterNavNode(child, c)).filter(Boolean);
    if (children.length === 0) return null;
    const hint = node.hintRequiresLeafId && !treeHasLeafId(children, node.hintRequiresLeafId)
      ? undefined
      : node.hint;
    return { ...node, children, hint };
  }
  if (!canShowAdminNavItem(node, c)) return null;
  return { ...node };
}

export function filterVisibleNav(sections, c) {
  return sections.map((section) => filterNavNode(section, c)).filter(Boolean);
}

function normalizeSearchText(value) {
  return String(value || '').trim().toLowerCase();
}

function buildNavSearchBlob(item, extra = []) {
  return [
    item?.label,
    item?.pageTitle,
    item?.breadcrumbLabel,
    item?.hint,
    ...(item?.searchTerms || []),
    ...getAdminNavSearchTerms(item?.id),
    ...extra,
  ]
    .map(normalizeSearchText)
    .filter(Boolean)
    .join(' ');
}

/**
 * 側欄搜尋：依關鍵字過濾可見區段／子項（大小寫不敏感）。
 * @param {AdminNavSection[]} sections
 * @param {string} query
 * @returns {AdminNavSection[]}
 */
function filterNavNodeByQuery(node, q, extraLabels) {
  const selfMatch = buildNavSearchBlob(node, extraLabels).includes(q);
  if (isNavGroup(node)) {
    if (selfMatch) return node;
    const nextExtra = [...extraLabels, node.label, node.hint].filter(Boolean);
    const children = node.children
      .map((child) => filterNavNodeByQuery(child, q, nextExtra))
      .filter(Boolean);
    if (children.length === 0) return null;
    return { ...node, children };
  }
  return selfMatch ? node : null;
}

export function filterAdminNavByQuery(sections, query) {
  const q = normalizeSearchText(query);
  if (!q) return sections;
  return sections.map((section) => filterNavNodeByQuery(section, q, [])).filter(Boolean);
}

/**
 * @param {string} pathname
 * @param {AdminNavContext} ctx
 * @param {{ leafOverride?: string }} [options]
 * @returns {{ label: string, to?: string }[]}
 */
export function getAdminBreadcrumbs(pathname, ctx, options = {}) {
  const { leafOverride } = options;
  const meta = getAdminPageMeta(pathname, ctx);
  const filtered = filterVisibleNav(ADMIN_NAV_SECTIONS, ctx);
  const section = filtered.find((s) => s.id === meta.sectionId);
  const groupPath = firstVisibleLeafPath(section) || section?.path;

  const trail = [{
    label: '後台',
    to: (() => {
      const homePath = getAdminRoleHomePath(ctx?.accessProfile);
      return !homePath || homePath === '/login' ? '/admin' : homePath;
    })(),
  }];
  if (meta.groupLabel && meta.groupLabel !== '後台') {
    trail.push({
      label: meta.groupLabel,
      to: groupPath || undefined,
    });
  }
  if (meta.navGroupLabel && meta.navGroupLabel !== meta.groupLabel) {
    trail.push({
      label: meta.navGroupLabel,
      to: meta.navGroupPath || undefined,
    });
  }
  if (meta.breadcrumbParent) {
    trail.push({
      label: meta.breadcrumbParent.label,
      to: meta.breadcrumbParent.to,
    });
  }
  trail.push({ label: leafOverride || meta.breadcrumbLeaf || meta.pageTitle });
  return trail;
}

/**
 * @param {string} pathname
 * @param {AdminNavContext} ctx
 */
export function getAdminPageTitle(pathname, ctx) {
  return getAdminPageMeta(pathname, ctx).pageTitle;
}

/**
 * Sidebar active：展開區段與作用中子項
 * @param {string} pathname
 * @param {AdminNavContext} ctx
 */
export function getSidebarActiveState(pathname, ctx) {
  const meta = getAdminPageMeta(pathname, ctx);
  return {
    sectionId: meta.sectionId,
    groupId: meta.groupId || null,
    groupIds: meta.groupIds || (meta.groupId ? [meta.groupId] : []),
    childId: meta.childId,
  };
}

/**
 * 側欄單層連結（無 children）是否為目前作用中
 * @param {{ sectionId: string|null, childId: string|null }} active
 * @param {string} sectionId
 */
export function isSidebarSingleSectionActive(active, sectionId) {
  return active.sectionId === sectionId;
}

/**
 * 側欄子連結是否為目前作用中
 * @param {{ sectionId: string|null, childId: string|null }} active
 * @param {string} sectionId
 * @param {string} leafId
 */
export function isSidebarChildActive(active, sectionId, leafId) {
  return active.sectionId === sectionId && active.childId === leafId;
}
